'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { authenticatedGet } from '../lib/api/authenticated-fetch.js';
import { logger } from '../lib/logger.js';

/**
 * ChatActivityContext — "which of my chats are working right now?"
 *
 * Two sources, deliberately layered:
 *
 * 1. **Mounted panes** report their own `useChat` status. Exact and instant, but
 *    only exists for a chat that is currently rendered (an open tab).
 * 2. **The server** (`GET {apiBaseUrl}/streams/active`) answers for everything
 *    else, so a chat that is only a sidebar row still shows as busy.
 *
 * A chat with a mounted pane is answered by the PANE, not the poll: the pane
 * knows the turn ended the instant it ends, while the session row takes until
 * the next poll to flip, which would leave a dot spinning on a finished chat.
 *
 * Also tracks "finished while you were looking elsewhere" so a background tab
 * can show an unread mark.
 */

/** What a mounted pane reports about itself. */
export type ChatRunStatus = 'idle' | 'streaming' | 'error';

export interface ChatActivityContextValue {
  /** True while a turn is running for this chat (pane truth, else server truth). */
  isChatStreaming: (chatId: string) => boolean;
  /** True when a turn finished for this chat while it was not the active one. */
  hasUnread: (chatId: string) => boolean;
  /** Called by a mounted pane whenever its `useChat` status changes. */
  reportChatStatus: (chatId: string, status: ChatRunStatus) => void;
  /** Called when a pane unmounts so its stale status stops shadowing the poll. */
  clearChatStatus: (chatId: string) => void;
  /** Drop the unread mark (the user looked at the chat). */
  markChatRead: (chatId: string) => void;
}

const ChatActivityContext = createContext<ChatActivityContextValue | undefined>(undefined);

/** How often the server is asked which turns are live. */
const ACTIVE_STREAMS_POLL_MS = 5000;

interface ActiveStreamsResponse {
  chatIds?: unknown;
}

function parseChatIds(response: ActiveStreamsResponse): string[] {
  const { chatIds } = response;
  if (!Array.isArray(chatIds)) return [];
  return chatIds.filter((id): id is string => typeof id === 'string');
}

export interface ChatActivityProviderProps {
  children: React.ReactNode;
  apiBaseUrl?: string;
  /** The chat the user is looking at; used to decide what counts as unread. */
  activeChatId: string | null;
  /**
   * Hosts without Meridian API access (extension pre-auth) pass `false` so the
   * poll never runs; pane-reported status still works.
   */
  pollEnabled?: boolean;
}

export function ChatActivityProvider({
  children,
  apiBaseUrl = '/api/meridian',
  activeChatId,
  pollEnabled = true,
}: ChatActivityProviderProps) {
  const [paneStatuses, setPaneStatuses] = useState<Record<string, ChatRunStatus>>({});
  const [unreadChatIds, setUnreadChatIds] = useState<Record<string, true>>({});

  // Read through a ref so `reportChatStatus` stays referentially stable — panes
  // pass it straight into an effect dependency list.
  const activeChatIdRef = useRef<string | null>(activeChatId);
  activeChatIdRef.current = activeChatId;

  const activeStreamsQuery = useQuery({
    queryKey: ['meridian', 'active-streams', apiBaseUrl],
    queryFn: async (): Promise<string[]> => {
      const response = await authenticatedGet<ActiveStreamsResponse>(
        `${apiBaseUrl}/streams/active`,
      );
      return parseChatIds(response);
    },
    enabled: pollEnabled,
    // TanStack pauses interval refetches while the window is blurred (default
    // `refetchIntervalInBackground: false`), so a backgrounded tab costs nothing.
    refetchInterval: ACTIVE_STREAMS_POLL_MS,
    staleTime: ACTIVE_STREAMS_POLL_MS,
    // Cosmetic data: a failed poll must not retry-storm or surface an error.
    retry: false,
  });

  const serverActiveChatIds = useMemo(
    () => new Set(activeStreamsQuery.data ?? []),
    [activeStreamsQuery.data],
  );

  // Previous status per chat, tracked outside React state so the unread
  // decision is made in the event, not inside a state updater — updaters must
  // stay pure (StrictMode runs them twice).
  const previousStatusRef = useRef<Record<string, ChatRunStatus>>({});

  const reportChatStatus = useCallback((chatId: string, status: ChatRunStatus) => {
    const previous = previousStatusRef.current[chatId];
    if (previous === status) return;
    previousStatusRef.current[chatId] = status;

    // A turn that ended somewhere the user was not looking earns an unread mark.
    if (previous === 'streaming' && status !== 'streaming' && chatId !== activeChatIdRef.current) {
      setUnreadChatIds((unread) => (unread[chatId] ? unread : { ...unread, [chatId]: true }));
    }

    setPaneStatuses((prev) => (prev[chatId] === status ? prev : { ...prev, [chatId]: status }));
  }, []);

  const clearChatStatus = useCallback((chatId: string) => {
    delete previousStatusRef.current[chatId];
    setPaneStatuses((prev) => {
      if (!(chatId in prev)) return prev;
      const next = { ...prev };
      delete next[chatId];
      return next;
    });
  }, []);

  const markChatRead = useCallback((chatId: string) => {
    setUnreadChatIds((prev) => {
      if (!prev[chatId]) return prev;
      const next = { ...prev };
      delete next[chatId];
      return next;
    });
  }, []);

  // Looking at a chat reads it.
  useEffect(() => {
    if (activeChatId) markChatRead(activeChatId);
  }, [activeChatId, markChatRead]);

  const isChatStreaming = useCallback(
    (chatId: string): boolean => {
      const paneStatus = paneStatuses[chatId];
      // Pane truth wins whenever a pane is mounted for this chat — see header.
      if (paneStatus !== undefined) return paneStatus === 'streaming';
      return serverActiveChatIds.has(chatId);
    },
    [paneStatuses, serverActiveChatIds],
  );

  const hasUnread = useCallback(
    (chatId: string): boolean => Boolean(unreadChatIds[chatId]),
    [unreadChatIds],
  );

  useEffect(() => {
    if (activeStreamsQuery.error) {
      logger.debug('active-streams poll failed', { error: String(activeStreamsQuery.error) });
    }
  }, [activeStreamsQuery.error]);

  const value = useMemo<ChatActivityContextValue>(
    () => ({ isChatStreaming, hasUnread, reportChatStatus, clearChatStatus, markChatRead }),
    [isChatStreaming, hasUnread, reportChatStatus, clearChatStatus, markChatRead],
  );

  return <ChatActivityContext.Provider value={value}>{children}</ChatActivityContext.Provider>;
}

/**
 * Chat activity, or `undefined` when no provider is mounted.
 *
 * Optional by design: `ChatItem` and friends also render in hosts that never
 * mount the provider (shared-chat views, extension pre-auth), and a missing
 * indicator is the correct degradation there.
 */
export function useChatActivityOptional(): ChatActivityContextValue | undefined {
  return useContext(ChatActivityContext);
}
